import { decodeBase64 } from '@portals/core/src/utilities/decodeBase64';
import { encodeBase64 } from '@portals/core/src/utilities/encodeBase64';
import { useCallback, useEffect, useMemo } from 'react';
import { useCookies } from 'react-cookie';

import { useBanks } from '../useBanks/useBanks';

interface Result {
  bankCode: string;
  bankName: string;

  setCookie: (bankCode: string, bankName: string, assignmentType: string) => void;
}

export const useSPKCookie = (): Result => {
  const [cookies, _setCookie] = useCookies(['SPK_COOKIE', 'SPK_COOKIE_EXTRAS']);

  const { getBanks } = useBanks();

  const cookie = useMemo(() => {
    return new URLSearchParams(decodeBase64(cookies.SPK_COOKIE));
  }, [cookies.SPK_COOKIE]);

  const cookieExtras = useMemo(() => {
    return new URLSearchParams(decodeBase64(cookies.SPK_COOKIE_EXTRAS));
  }, [cookies.SPK_COOKIE_EXTRAS]);

  const bankCode = useMemo(() => cookie.get('bankcode'), [cookie]);
  const bankName = useMemo(() => cookieExtras.get('bankname'), [cookieExtras]);

  const setCookie = useCallback(
    (bankCode, bankName, assignmentType = 'unknown') => {
      let expirationDate = new Date();

      const cookieExtras = new URLSearchParams([
        ['source', assignmentType],
        ['bankcode', bankCode],
        ['bankname', bankName],
      ]);

      if (typeof assignmentType === 'string' && assignmentType.toLowerCase() === 'geolocation') {
        expirationDate = new Date(expirationDate.getTime() + 1000 * 60 * 60 * 24 * 14);
        cookieExtras.set('accuracy', 'low');
      } else if (typeof assignmentType === 'string' && assignmentType.toLowerCase() !== 'unknown') {
        expirationDate = new Date(expirationDate.getTime() + 1000 * 60 * 60 * 24 * 180);
        cookieExtras.set('accuracy', 'high');
      } else {
        expirationDate = new Date(expirationDate.getTime() + 1000 * 60 * 60 * 24 * 14);
      }

      cookieExtras.sort();

      _setCookie('SPK_COOKIE_EXTRAS', encodeBase64(cookieExtras.toString()), {
        expires: expirationDate,
      });

      _setCookie('SPK_COOKIE', encodeBase64(`bankcode=${bankCode}`), {
        expires: expirationDate,
      });
    },
    [_setCookie]
  );

  useEffect(() => {
    const currentBankCode = cookie.get('bankcode') || cookieExtras.get('bankcode');

    const fetchBank = async () => {
      const banks = await getBanks(currentBankCode);
      if (banks.length) {
        setCookie(banks[0].bankCode, banks[0].name, cookieExtras.get('source'));
      }
    };

    if (cookie.get('bankcode') !== cookieExtras.get('bankcode')) {
      fetchBank().catch((e) => {
        // eslint-disable-next-line no-console
        console.error(e);
      });
    } else if (!cookieExtras.get('bankname') && currentBankCode) {
      fetchBank().catch((e) => {
        // eslint-disable-next-line no-console
        console.error(e);
      });
    }
  }, [cookie, cookieExtras, getBanks, setCookie]);

  return { bankCode, bankName, setCookie };
};
